import * as React from "react"

interface DismissExclusionsContextValue {
  registerExclusion: (ref: React.RefObject<HTMLElement | null>) => () => void
}

const DismissExclusionsContext = React.createContext<DismissExclusionsContextValue | null>(null)

/**
 * Provider that allows elements to register themselves as "excluded" from
 * outside-click detection. This prevents Radix UI modals/sheets/popovers
 * from closing when clicking on excluded elements (like the chat widget).
 *
 * Uses a document-level capture phase event listener to intercept pointerdown
 * events BEFORE Radix UI's DismissableLayer can detect them as "outside" clicks.
 */
export function DismissExclusionsProvider({ children }: { children: React.ReactNode }) {
  const exclusionsRef = React.useRef<Set<React.RefObject<HTMLElement | null>>>(new Set())

  const registerExclusion = React.useCallback((ref: React.RefObject<HTMLElement | null>) => {
    exclusionsRef.current.add(ref)
    return () => {
      exclusionsRef.current.delete(ref)
    }
  }, [])

  // Check if a target is inside any excluded element
  const isExcluded = React.useCallback((target: EventTarget | null) => {
    if (!target || !(target instanceof Node)) return false
    const refs = Array.from(exclusionsRef.current)
    for (let i = 0; i < refs.length; i++) {
      if (refs[i].current?.contains(target)) return true
    }
    return false
  }, [])

  // Add capture phase listener to intercept events BEFORE Radix sees them
  React.useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (isExcluded(e.target)) {
        // Use stopImmediatePropagation to prevent ALL other listeners on document from firing
        // Regular stopPropagation only prevents propagation to child elements, but other
        // listeners on the same element (document) would still fire
        e.stopImmediatePropagation()
      }
    }

    // Capture phase listener (third arg = true) - fires BEFORE bubbling phase
    document.addEventListener("pointerdown", handlePointerDown, true)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true)
    }
  }, [isExcluded])

  const value = React.useMemo(() => ({ registerExclusion }), [registerExclusion])

  return (
    <DismissExclusionsContext.Provider value={value}>
      {children}
    </DismissExclusionsContext.Provider>
  )
}

/**
 * Hook to register an element as excluded from outside-click detection.
 * The element will be unregistered when the component unmounts.
 *
 * @example
 * ```tsx
 * function ChatWidget() {
 *   const containerRef = useRef<HTMLDivElement>(null)
 *   useRegisterDismissExclusion(containerRef)
 *   return <div ref={containerRef}>...</div>
 * }
 * ```
 */
export function useRegisterDismissExclusion(ref: React.RefObject<HTMLElement | null>) {
  const context = React.useContext(DismissExclusionsContext)
  React.useEffect(() => {
    if (!context) return
    return context.registerExclusion(ref)
  }, [context, ref])
}
